import dayjs from 'dayjs';
import { fromEnumAsString, toEnumFromString } from '../../../serialization/enum-utils.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../../dto/data-model.js';
import { PassEventValidityRule } from './pass-event-validity-rule.js';
import { cloneMap, isSameData, isSameMap, mapToData } from '../../dto/data-model-utils.js';
import { IEventOpenAccessProtocol, OpenAccessProtocol, OpenAccessProtocolType } from './open-access-protocol.js';
import { MigrationSafe } from '../../core/migration-safe.js';
import { Cardinal } from '../../core/cardinal.js';
import { JSONUtils } from '../../../utility/json-utils.js';
import { VersionRange } from '../../core/version-range.js';
import { BooleanOrListOfString } from '../../core/bool-or-list.js';

export class EventOpenAccessProtocol extends OpenAccessProtocol implements IEventOpenAccessProtocol {
  static readonly TYPE_ID: string = '63db964b2656479a943c4bf0c1a1af7f';

  static readonly SCHEMA_VERSION: number = 4;

  public readonly type: OpenAccessProtocolType = OpenAccessProtocolType.PerEvent;

  private constructor(
    public readonly eventValidityRule: PassEventValidityRule,

    // Keyed by event ID. List of strings represents compatible pass variations.
    public readonly eventConfiguration: Map<string, BooleanOrListOfString>,
    public readonly maxEventCount: MigrationSafe<Cardinal>,
    public readonly startDate: dayjs.Dayjs | null,
    public readonly expirationDate: dayjs.Dayjs | null,
  ) {
    super();
  }

  get $typeId(): string {
    return EventOpenAccessProtocol.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(EventOpenAccessProtocol.SCHEMA_VERSION);
  }

  private static create(args: {
    eventValidityRule: PassEventValidityRule,
    eventConfiguration: Map<string, BooleanOrListOfString>,
    maxEventCount: MigrationSafe<Cardinal>,
    startDate?: dayjs.Dayjs | null,
    expirationDate?: dayjs.Dayjs | null,
  }): EventOpenAccessProtocol {
    return new EventOpenAccessProtocol(
        args.eventValidityRule,
        args.eventConfiguration,
        args.maxEventCount,
        args.startDate ?? null,
        args.expirationDate ?? null,
    );
  }

  static forTemplate(args: {
    eventValidityRule: PassEventValidityRule,
    eventConfiguration: Map<string, BooleanOrListOfString>,
    startDate?: dayjs.Dayjs | null,
    expirationDate?: dayjs.Dayjs | null,
  }): EventOpenAccessProtocol {
    return this.create({
      ...args,
      maxEventCount: MigrationSafe.some(Cardinal.finite(0)),
    });
  }

  static forInstance(args: {
    eventValidityRule: PassEventValidityRule,
    eventConfiguration: Map<string, BooleanOrListOfString>,
    maxEventCount: Cardinal,
    startDate?: dayjs.Dayjs | null,
    expirationDate?: dayjs.Dayjs | null,
  }): EventOpenAccessProtocol {
    return this.create({
      ...args,
      maxEventCount: MigrationSafe.some(args.maxEventCount.clone()),
    });
  }

  static fromMap(context: SerializationContext, map: SerializedData): EventOpenAccessProtocol {
    return this.create({
      eventValidityRule: toEnumFromString(MapUtils.getString(map, 'eventValidityRule'), PassEventValidityRule),
      eventConfiguration: MapUtils.getMap<string, BooleanOrListOfString>(
          map,
          'eventConfiguration',
          (key, value) => ({ key: key, value: BooleanOrListOfString.decode(value) })),
      maxEventCount: MigrationSafe.decode(
          MapUtils.getRaw(map, 'maxEventCount'),
          (data) => Cardinal.decode(JSONUtils.decodeNumber(data))),
      startDate: context.decodeDateOrNull(MapUtils.getNumberOrNull(map, 'startDate')),
      expirationDate: context.decodeDateOrNull(MapUtils.getNumberOrNull(map, 'expirationDate')),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'type': fromEnumAsString(this.type),
      'eventValidityRule': fromEnumAsString(this.eventValidityRule),
      'eventConfiguration': mapToData(
          this.eventConfiguration,
          (key, value) => ({ key: key, value: value.encode() }),
      ),
      'maxEventCount': this.maxEventCount.encode((value) => (value.encode())),
      'startDate': context.encodeDateOrNull(this.startDate),
      'expirationDate': context.encodeDateOrNull(this.expirationDate),
    };
  }

  clone(): this {
    return EventOpenAccessProtocol.create({
      eventValidityRule: this.eventValidityRule,
      eventConfiguration: cloneMap(this.eventConfiguration, (v) => v.clone()),
      maxEventCount: this.maxEventCount.clone((value) => value.clone()),
      startDate: this.startDate,
      expirationDate: this.expirationDate,
    }) as this;
  }
  
  copyWith({
    eventValidityRule = this.eventValidityRule,
    eventConfiguration = cloneMap(this.eventConfiguration, (v) => v.clone()),
    maxEventCount = this.maxEventCount.clone((value) => value.clone()),
    startDate = this.startDate,
    expirationDate = this.expirationDate,
  }: {
    eventValidityRule?: PassEventValidityRule,
    eventConfiguration?: Map<string, BooleanOrListOfString>,
    maxEventCount?: MigrationSafe<Cardinal>,
    startDate?: dayjs.Dayjs | null,
    expirationDate?: dayjs.Dayjs | null,
  } = {}): this {
    return EventOpenAccessProtocol.create({
      eventValidityRule,
      eventConfiguration,
      maxEventCount,
      startDate,
      expirationDate,
    }) as this;
  }

  isSame(other: EventOpenAccessProtocol): boolean {
    if(other === this) {
      return true;
    }

    return other.maxEventCount.isSame(this.maxEventCount, (other, self) => other.isSame(self)) &&
            other.eventValidityRule === this.eventValidityRule &&
            isSameMap(other.eventConfiguration, this.eventConfiguration, isSameData) &&
            other.startDate?.valueOf() === this.startDate?.valueOf() &&
            other.expirationDate?.valueOf() === this.expirationDate?.valueOf();
  }

  applyTemplateChanges(other: this): this {
    return EventOpenAccessProtocol.create({
      eventValidityRule: this.eventValidityRule,
      eventConfiguration: cloneMap(other.eventConfiguration, (v) => v.clone()),
      maxEventCount: this.maxEventCount.clone((value) => value.clone()),
      startDate: this.startDate,
      expirationDate: this.expirationDate,
    }) as this;
  }

  cloneEventConfiguration(): Map<string, BooleanOrListOfString> {
    return cloneMap(this.eventConfiguration, (v) => v.clone());
  }

  isEventAssignedToPass({ eventId, passVariationId }: {
    eventId: string,
    passVariationId: string
  }): boolean {
    const entry = this.eventConfiguration.get(eventId);
    const isInEventConfiguration = entry != null && (
      (entry.isList && entry.listValue.includes(passVariationId)) ||
      (entry.isBoolean && entry.booleanValue)
    );

    return this.eventValidityRule === PassEventValidityRule.IncludedEvents ? isInEventConfiguration : !isInEventConfiguration;
  }

  isEventAssignedToTemplate({ eventId }: { eventId: string }): boolean {
    const entry = this.eventConfiguration.get(eventId);
    if(this.eventValidityRule === PassEventValidityRule.IncludedEvents) {
      return entry != null && (
        (entry.isList && entry.listValue.length > 0) ||
        (entry.isBoolean && entry.booleanValue)
      );
    }

    return entry == null || entry.isList || (entry.isBoolean && !entry.booleanValue);
  }

  getAssignedEventIdsForPass({ passVariationId }: { passVariationId: string }): string[] {
    const assignedEventIds: string[] = [];
    for(const eventId of this.eventConfiguration.keys()) {
      if(this.isEventAssignedToPass({ eventId, passVariationId })) {
        assignedEventIds.push(eventId);
      }
    }

    return assignedEventIds;
  }

  static verifyType(dm: DataModel): boolean {
    return dm instanceof EventOpenAccessProtocol;
  }

  static assertType(dm: DataModel): dm is EventOpenAccessProtocol {
    return dm instanceof EventOpenAccessProtocol;
  }

  static convertToLegacyEventList(protocol: EventOpenAccessProtocol): string[] | null {
    return IEventOpenAccessProtocol.convertToLegacyEventList(protocol.eventConfiguration);
  }
}

export class EventOpenAccessProtocolBuilder extends DataModelBuilder<EventOpenAccessProtocol> {
  build(context: SerializationContext, map: SerializedData): EventOpenAccessProtocol {
    return EventOpenAccessProtocol.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(EventOpenAccessProtocol.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(EventOpenAccessProtocol.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // Version 2:
    // - add 'maxEventCount' field of type `MigrationSafe<Cardinal>`.
    if(version < 2) {
      map['maxEventCount'] ??= MigrationSafe.UNDEFINED_STATUS;

      version = 2;
    }

    // Version 3:
    // - changed `event` from `string[]` to `Map<string, BooleanOrListOfString>.
    if(version < 3) {
      const events = IEventOpenAccessProtocol.convertFromLegacyEventList(
          MapUtils.getList<string>(map, 'events', (item) => JSONUtils.decodeString(item))
      );

      map['events'] = mapToData(
          events,
          (key, value) => ({ key: key, value: value.encode() }),
      );

      version = 3;
    }

    // Version 4:
    // - add 'eventValidityRule' field of type `PassEventValidityRule`.
    // - rename 'events' to 'eventConfiguration'.
    // - add optional 'startDate' and 'expirationDate' fields of type `dayjs.Dayjs`.
    if(version < 4) {
      const includedEvents = MapUtils.getMap<string, BooleanOrListOfString>(
          map,
          'events',
          (key, value) => ({ key: key, value: BooleanOrListOfString.decode(value) }),
      );
      const now = dayjs();

      map['eventValidityRule'] ??= fromEnumAsString(PassEventValidityRule.IncludedEvents);
      map['eventConfiguration'] ??= includedEvents;
      map['startDate'] ??= context.encodeDate(now);
      map['expirationDate'] ??= context.encodeDate(now.add(1000, 'year'));

      version = 4;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
